import React, { useState } from 'react';
import { Box, Text, HStack, VStack } from '@chakra-ui/react';

interface TimePickerProps {
  selectedTime: string | null;
  onTimeSelect: (time: string) => void;
  onClose: () => void;
  type?: 'start' | 'end';
  startTime?: string; // For end time validation
}

const TimePicker: React.FC<TimePickerProps> = ({
  selectedTime,
  onTimeSelect,
  onClose,
  type = 'start',
  startTime
}) => {
  const [currentHour, setCurrentHour] = useState(() => {
    if (selectedTime) {
      const [time] = selectedTime.split(' ');
      const [hour] = time.split(':');
      return parseInt(hour);
    }
    return 12; // Default to 12 PM
  });

  const [currentMinute, setCurrentMinute] = useState(() => {
    if (selectedTime) {
      const [time] = selectedTime.split(' ');
      const [, minute] = time.split(':');
      return parseInt(minute);
    }
    return 0; // Default to :00
  });

  const [period, setPeriod] = useState<'am' | 'pm'>(() => {
    if (selectedTime) {
      const [, period] = selectedTime.split(' ');
      return period.toLowerCase() as 'am' | 'pm';
    }
    return 'pm'; // Default to PM
  });


  const formatTime = (hour: number, minute: number, period: 'am' | 'pm') => {
    const minuteStr = minute.toString().padStart(2, '0');
    return `${hour}:${minuteStr} ${period}`;
  };

  const isValidTime = (hour: number, minute: number, period: 'am' | 'pm') => {
    if (type === 'end' && startTime) {
      // Parse start time
      const [startTimeStr, startPeriod] = startTime.split(' ');
      const [startHour, startMinute] = startTimeStr.split(':').map(Number);

      // Convert to 24-hour format for comparison
      let start24Hour = startHour;
      if (startPeriod.toLowerCase() === 'pm' && startHour !== 12) {
        start24Hour += 12;
      } else if (startPeriod.toLowerCase() === 'am' && startHour === 12) {
        start24Hour = 0;
      }

      let end24Hour = hour;
      if (period === 'pm' && hour !== 12) {
        end24Hour += 12;
      } else if (period === 'am' && hour === 12) {
        end24Hour = 0;
      }

      // Check if end time is after start time
      if (end24Hour < start24Hour) return false;
      if (end24Hour === start24Hour && minute <= startMinute) return false;
    }
    return true;
  };

  const handleTimeSelect = () => {
    if (isValidTime(currentHour, currentMinute, period)) {
      const formattedTime = formatTime(currentHour, currentMinute, period);
      onTimeSelect(formattedTime);
      onClose();
    }
  };

  const navigateHour = (direction: 'up' | 'down') => {
    let newHour = currentHour;
    if (direction === 'up') {
      newHour = currentHour === 12 ? 1 : currentHour + 1;
    } else {
      newHour = currentHour === 1 ? 12 : currentHour - 1;
    }
    setCurrentHour(newHour);
  };

  const navigateMinute = (direction: 'up' | 'down') => {
    let newMinute = currentMinute;
    if (direction === 'up') {
      newMinute = currentMinute === 59 ? 0 : currentMinute + 1;
    } else {
      newMinute = currentMinute === 0 ? 59 : currentMinute - 1;
    }
    setCurrentMinute(newMinute);
  };

  const togglePeriod = () => {
    setPeriod(period === 'am' ? 'pm' : 'am');
  };

  return (
    <Box
      position="absolute"
      bottom="100%"
      left="50%"
      transform="translateX(-50%)"
      width="280px"
      height="240px"
      backgroundColor="#120F1B"
      borderRadius="13px"
      border="1px solid"
      borderColor="rgba(29, 27, 38, 1)"
      boxShadow="0px 10px 60px 0px rgba(0, 0, 0, 0.1), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
      zIndex={1000}
      padding="16px"
      paddingBottom="20px"
      marginBottom="8px"
      data-timepicker="true"
    >
      {/* Header */}
      {/* <Text
        //className="static-text-white"
        fontFamily="Poppins, sans-serif"
        fontWeight="500"
        fontSize="16px"
        lineHeight="1em"
        color="#C6D959"
        textAlign="center"
        marginBottom="16px"
      >
        Select {type === 'start' ? 'Start' : 'End'} Time
      </Text> */}

      {/* Time Display */}
      <HStack justifyContent="center" spacing="8px" marginBottom="16px" alignItems="center">
        {/* Hour */}
        <VStack spacing="4px" alignItems="center">
          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={() => navigateHour('up')}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 1L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>

          <Text className="title-text">
            {currentHour.toString().padStart(2, '0')}
          </Text>

          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={() => navigateHour('down')}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 7L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        </VStack>

        {/* Colon */}
        <Text className="title-text">
          :
        </Text>

        {/* Minute */}
        <VStack spacing="4px" alignItems="center">
          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={() => navigateMinute('up')}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 1L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>

          <Text className="title-text">
            {currentMinute.toString().padStart(2, '0')}
          </Text>

          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={() => navigateMinute('down')}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 7L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        </VStack>

        {/* AM/PM */}
        <VStack spacing="4px" alignItems="center">
          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={togglePeriod}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 1L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>

          <Text
            className="title-text"
            textTransform="uppercase"
          >
            {period}
          </Text>

          <Box
            width="40px"
            height="40px"
            cursor="pointer"
            onClick={togglePeriod}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: 0.7 }}
          >
            <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
              <path d="M1 4L4 7L7 4" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        </VStack>
      </HStack>

      {/* Action Buttons */}
      <HStack justifyContent="center" spacing="12px">
        <Box
          width="80px"
          height="32px"
          backgroundColor={isValidTime(currentHour, currentMinute, period) ? "#C6D959" : "#595C5D"}
          borderRadius="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={isValidTime(currentHour, currentMinute, period) ? "pointer" : "not-allowed"}
          onClick={isValidTime(currentHour, currentMinute, period) ? handleTimeSelect : undefined}
          _hover={isValidTime(currentHour, currentMinute, period) ? { backgroundColor: "#b8cc4a" } : {}}
        >
          <Text
            className="static-text-white"
            color={isValidTime(currentHour, currentMinute, period) ? "#020105" : "#979C9E"}
          >
            Done
          </Text>
        </Box>
      </HStack>
    </Box>
  );
};

export default TimePicker;
